import { ExitIcon } from '@daily/shared/components/Icons/ExitIcon';
import { ReturnIcon } from '@daily/shared/components/Icons/ReturnIcon';
import { Popover } from '@daily/shared/components/Popover';
import { Text } from '@daily/shared/components/Text';
import { useTheme } from '@daily/shared/contexts/Theme';
import { useRecording } from '@daily-co/daily-react';
import { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { useBreakouts } from '../../contexts/BreakoutsProvider';
import { useCallState } from '../../contexts/CallProvider';
import { robotsClassName } from '../../lib/robots';
import { useLeaveWhileRecordingModal } from '../../lib/state/recordings';
import { useBreakoutSessionState } from '../BreakoutRooms/hooks/useBreakoutSessionState';
import { BreakoutLeaveMenuContent } from './BreakoutLeaveMenuContent';
import { TrayButton } from './TrayButton';

interface Props {
  id: string;
}

export const LeaveButton: React.FC<Props> = ({ id }) => {
  const { t } = useTranslation();
  const { colors } = useTheme();
  const { enableCallButtons, leaveCall } = useCallState();
  const [, setShowLeaveWhileRecordingModal] = useLeaveWhileRecordingModal();
  const { isRecording, local } = useRecording();
  const [showBreakoutLeaveMenu, setShowBreakoutLeaveMenu] = useState(false);
  const { hasJoinedBreakout } = useBreakoutSessionState();
  const { leaveBreakout } = useBreakouts();

  const leaveCallCompletely = useCallback(() => {
    if (isRecording && local) {
      setShowLeaveWhileRecordingModal(true);
      return;
    }
    // In case Picture in Picture is active, end it
    try {
      // @ts-ignore
      if (document.pictureInPictureElement) {
        // @ts-ignore
        document.exitPictureInPicture();
      }
    } catch {}
    leaveCall();
  }, [isRecording, leaveCall, local, setShowLeaveWhileRecordingModal]);

  const handleClick = useCallback(() => {
    if (hasJoinedBreakout) {
      setShowBreakoutLeaveMenu(!showBreakoutLeaveMenu);
      return;
    }

    leaveCallCompletely();
  }, [
    hasJoinedBreakout,
    leaveCallCompletely,
    setShowBreakoutLeaveMenu,
    showBreakoutLeaveMenu,
  ]);

  const handleClickLeaveRoom = () => {
    leaveBreakout(true);
    setShowBreakoutLeaveMenu(false);
  };

  const handleCloseLeaveMenu = () => {
    setShowBreakoutLeaveMenu(false);
  };

  useEffect(() => {
    if (typeof navigator.mediaSession?.setActionHandler !== 'function') return;
    try {
      navigator.mediaSession.setActionHandler('hangup', handleClick);
    } catch (e) {}
  }, [handleClick]);

  return (
    <div className="leave">
      <TrayButton
        aria-controls={hasJoinedBreakout ? 'breakout-leave-menu' : undefined}
        className={robotsClassName(id)}
        disabled={!enableCallButtons}
        id={id}
        onClick={handleClick}
      >
        {hasJoinedBreakout ? (
          <ReturnIcon color={colors.system.red} />
        ) : (
          <ExitIcon color={colors.system.red} />
        )}
        <Text>
          {hasJoinedBreakout
            ? t('general.exitWithOptions')
            : t('general.leave')}
        </Text>
      </TrayButton>
      {showBreakoutLeaveMenu && (
        <Popover
          align="right"
          aria-labelledby={id}
          id="breakout-leave-menu"
          placement="top"
          onClose={handleCloseLeaveMenu}
        >
          <BreakoutLeaveMenuContent
            onClickLeaveCall={leaveCallCompletely}
            onClickLeaveRoom={handleClickLeaveRoom}
          />
        </Popover>
      )}
    </div>
  );
};
